import React from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import Row from 'react-bootstrap/Row';
import axios from 'axios';
import * as yup from 'yup';
import {Formik} from 'formik';
import {useStoreActions, useStoreState} from 'easy-peasy';
import SearchSelect from '@/components/search_select/SearchSelect';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

interface EmailFetchAccount {
    id: number;
    email: string;
    credentialsId: number;
}

interface FormValues {
    email: string;
    credentialsId: number;
}

interface EmailFetchAccountsSettingsFormProps {
    objId?: number;
    onSave?: () => void;
}

const emailFetchAccountsSchema = yup.object({
    email: yup.string()
        .email(i18n.t('jt_email_valid.text'))
        .max(255, i18n.t('jt_email_max_length_255.text'))
        .required(i18n.t('jt_email_required.text')),
    credentialsId: yup.number()
        .integer(i18n.t('jt_credentials_valid_integer.text'))
        .required(i18n.t('jt_credentials_required.text'))
        .min(1, i18n.t('jt_credentials_min_1.text')),
});

const EmailFetchAccountsSettingsForm: React.FC<EmailFetchAccountsSettingsFormProps> = React.memo((
    {
        objId = 0,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const setSettingsModalOpen = useStoreActions((actions: any) => actions.settingsModalOpen.setSettingsModalOpen);

    const accountsList = useStoreState((state: any) => state.emailFetchAccounts.items);

    const addOrEditAction = useStoreActions((actions: any) => actions.emailFetchAccounts.addOrEdit);

    const onSubmit = async (values: FormValues) => {
        let requestMethod;
        let url = window.URLS.emailFetchAccountsResource;
        if (objId) {
            url = `${url}/${objId}`;
            requestMethod = axios.put;
        } else {
            requestMethod = axios.post;
        }

        const response = await requestMethod(url, values);

        setSettingsModalOpen(false);
        addOrEditAction(response.data);

        if (objId) {
            showSuccess(i18n.t('to_email_fetch_acc_ch.text'));
        } else {
            showSuccess(i18n.t('to_email_fetch_acc_cr.text'));
        }

        onSave?.();
    };

    return (
        <Formik
            initialValues={accountsList.find((el: EmailFetchAccount) => el.id === objId) || {
                email: '',
                credentialsId: 0,
            }}
            onSubmit={onSubmit}
            validationSchema={emailFetchAccountsSchema}
        >
            {(formik) => (
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="email" >
                                <Form.Label>{i18n.t('jt_email_address.text')}</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    name="email"
                                    isInvalid={formik.touched.email && !!formik.errors.email}
                                    autoFocus
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="credentialsId" >
                                <Form.Label>{i18n.t('jt_select_the_credentials_that_will_be_used_to_access_this_account.text')}</Form.Label>
                                <SearchSelect
                                    optionsUrl={window.URLS.emailFetchCredentialsResource}
                                    formik={formik}
                                    fieldName="credentialsId"
                                    valueName="id"
                                    labelName="email"
                                    isPaginated={true}
                                />
                                <Form.Control.Feedback
                                    type="invalid"
                                    style={{
                                        display: formik.touched.credentialsId && formik.errors.credentialsId ? 'block' : 'none',
                                    }}
                                >
                                    {formik.errors.credentialsId}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Form.Group className="ml-auto mb-3" controlId="action-save">
                            <Button
                                variant="info"
                                fullWidth
                                type="submit"
                                data-cy="email-fetch-accounts-settings-info-btn">
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Form.Group>
                    </Row>
                </Form >
            )}
        </Formik >
    );
});


export default EmailFetchAccountsSettingsForm; 